import { Box, Icon } from "@mui/material";
import { type KeyboardEvent as ReactKeyboardEvent, useEffect, useRef, useState } from "react";

import { BodyTextSmall, CaptionText } from "~/components/Typographies";
import { detectIsMac, isReservedBinding } from "~/keyboard/bindings";
import {
	hasComboModifier,
	hasMacAltGlyphIssue,
	isModifierKey,
	MAX_CHORD_STEPS,
	type RecordedKey,
	recordedKeyFromEvent,
	serializeRecording
} from "~/keyboard/shortcutRecorder";

const chordWindowMs = 800;

interface ShortcutRecorderFieldProps {
	onCancel: () => void;
	onCommit: (binding: string) => void;
}

const ShortcutRecorderField = ({ onCancel, onCommit }: ShortcutRecorderFieldProps) => {
	const captureRef = useRef<HTMLDivElement>(null);
	const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
	const pendingRef = useRef<RecordedKey[]>([]);
	const [pendingLabel, setPendingLabel] = useState("");
	const [error, setError] = useState<string | null>(null);
	const isMac = detectIsMac();

	const clearTimer = () => {
		if (timerRef.current) clearTimeout(timerRef.current);
		timerRef.current = undefined;
	};

	useEffect(() => {
		captureRef.current?.focus();
		return clearTimer;
	}, []);

	const setPending = (keys: RecordedKey[]) => {
		pendingRef.current = keys;
		setPendingLabel(keys.length > 0 ? serializeRecording(keys) : "");
	};

	const finalize = (keys: RecordedKey[]) => {
		clearTimer();
		if (keys.length === 0) return;
		if (keys.some((key) => hasMacAltGlyphIssue(key, isMac))) {
			setPending([]);
			setError("Option (Alt) shortcuts don't work reliably on macOS. Pick a different combination.");
			return;
		}
		const binding = serializeRecording(keys);
		if (isReservedBinding(binding)) {
			setPending([]);
			setError("That combination is reserved by the browser or app. Pick another.");
			return;
		}
		onCommit(binding);
	};

	const handleKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>) => {
		event.preventDefault();
		event.stopPropagation();
		if (event.key === "Escape") {
			clearTimer();
			onCancel();
			return;
		}
		const recorded = recordedKeyFromEvent(event.nativeEvent);
		if (isModifierKey(recorded)) return;
		setError(null);
		if (hasComboModifier(recorded)) {
			finalize(pendingRef.current.length > 0 ? pendingRef.current : [recorded]);
			return;
		}
		const next = [...pendingRef.current, recorded];
		if (next.length >= MAX_CHORD_STEPS) {
			finalize(next);
			return;
		}
		setPending(next);
		clearTimer();
		timerRef.current = setTimeout(() => finalize(next), chordWindowMs);
	};

	return (
		<Box
			aria-label="Recording shortcut"
			onBlur={onCancel}
			onKeyDown={handleKeyDown}
			ref={captureRef}
			role="textbox"
			sx={{
				alignItems: "center",
				border: 2,
				borderColor: error ? "error.main" : "primary.main",
				borderRadius: 1,
				display: "flex",
				gap: 1,
				minWidth: 220,
				outline: "none",
				px: 1.5,
				py: 0.75
			}}
			tabIndex={0}
		>
			<Icon sx={{ color: "error.main", fontSize: "1rem" }}>fiber_manual_record</Icon>
			<Box sx={{ flex: 1, minWidth: 0 }}>
				<BodyTextSmall bold>{pendingLabel || "Press keys…"}</BodyTextSmall>
				{error ? (
					<CaptionText color="error.main">{error}</CaptionText>
				) : (
					<CaptionText color="text.secondary">Press a combination or two keys for a chord. Esc to cancel.</CaptionText>
				)}
			</Box>
		</Box>
	);
};

export default ShortcutRecorderField;
